import { FC, useState, useEffect } from 'react'
import { getUserById } from '../core/_requests'
import { errorToast, successToast } from '../../../../../../_metronic/helpers/components/Toaster'
import { useQueryResponse } from '../core/QueryResponseProvider'
import { useListView } from '../core/ListViewProvider'
import { useAuth } from '../../../../auth'

type Props = {
  isUserLoading: boolean
  user?: any
  onDataReceived: (data: any) => void  // Add this prop to pass data back to parent
  onClose: () => void
  startDate: Date | null;
  endDate: Date | null;              // Add this prop to close the modal
}

const WfdItems: FC<Props> = ({ user = {}, isUserLoading, onDataReceived, onClose, startDate, endDate }) => {
  const [data, setData] = useState<any[]>([])
  const [tableData, setTableData] = useState<any[]>([])
  const [selectedItems, setSelectedItems] = useState<string[]>([])
  const [loading, setLoading] = useState(false)
  const { setItemIdForUpdate } = useListView()
  const { refetch } = useQueryResponse()
  const { currentUser, isSupplyChainManager, auth }: any = useAuth()

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res: any = await getUserById('clients/101212/categories')
        const resData = res
        setData(resData)
      } catch (error) {
        console.error('Error fetching categories', error)
      }
    }
    fetchCategories()
  }, [])


  // Add this function inside the WfdItems component
  const formatDate = (date: Date | null) => {
    if (!date) return '';
    const d = new Date(date);
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const year = d.getFullYear();
    return `${month}/${day}/${year}`;
  };

  useEffect(() => {
    if (data && data.length) {
      const dropdownData = data.map((eachData) => ({
        label: eachData.name,
        value: eachData.id,
      }))
      fetchItemData(dropdownData)
    }
  }, [data])

  const fetchItemData = async (dropdownData: any[]) => {
    try {
      const promises = dropdownData.map(async (category) => {
        if (!category.value) return []

        const res: any = await getUserById(`categories/${category.value}/price`)
        const resData = res

        return resData.map((item: any) => ({
          categoryId: item.categoryId,
          itemId: item.itemId,
          category: category.label,
          item: item.itemName || item.name,
        }))
      })

      const allResults = await Promise.all(promises)
      const mergedResults = allResults.flat()

      setTableData(mergedResults)
    } catch (error) {
      console.error('Error fetching item data', error)
    }
  }

  const cancel = () => {
    onClose()
  }

  const handleCheckboxChange = (itemId: string) => {
    setSelectedItems(prev => {
      if (prev.includes(itemId)) {
        return prev.filter(id => id !== itemId)
      } else {
        return [...prev, itemId]
      }
    })
  }

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedItems(tableData.map(item => item.itemId))
    } else {
      setSelectedItems([])
    }
  }

  const handleSubmit = async () => {
    if (selectedItems.length === 0) {
      errorToast('Please select at least one item')
      return
    }

    setLoading(true)
    try {
      const itemIds = selectedItems.join(',')
      const franchiseId = isSupplyChainManager ? auth?.data?.franchiseId : currentUser.id



      // Format dates
      const startDateStr = formatDate(startDate);
      const endDateStr = formatDate(endDate);

      // Build query parameters
      let queryParams = `?itemIds=${encodeURIComponent(itemIds)}`;
      if (startDateStr && endDateStr) {
        queryParams += `&start_date=${startDateStr}&end_date=${endDateStr}`;
      } else if (startDateStr) {
        queryParams += `&start_date=${startDateStr}`;
      } else if (endDateStr) {
        queryParams += `&end_date=${endDateStr}`;
      }


      // console.log("franchiseId", franchiseId)

      const response = await fetch(
        // `${process.env.REACT_APP_BASE_API_URL}sankey/franchise/multi/${franchiseId}?itemIds=${encodeURIComponent(itemIds)}`,
        `${process.env.REACT_APP_BASE_API_URL}sankey/franchise/multi/${franchiseId}${queryParams}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${auth?.data?.token}`,
            'Content-Type': 'application/json'
          }
        }
      )

      const responseData = await response.json()
      console.log('API Response:', responseData)

      // Pass the response data back to parent component
      onDataReceived(responseData)

      successToast('Items successfully fetched')

      // Close the modal
      onClose()
    } catch (error) {
      console.error('Error fetching data:', error)
      errorToast('Failed to fetch items')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div>
      <h2>Category Items</h2>

      <div style={{ marginBottom: '20px' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%' }} cellPadding={10} cellSpacing={0}>
          <thead>
            <tr>
              <th style={{ border: '1px solid #ccc', padding: '8px' }}>Category</th>
              <th style={{ border: '1px solid #ccc', padding: '8px' }}>Item</th>
              <th style={{ border: '1px solid #ccc', padding: '8px' }}>
                Select
                <input
                  type="checkbox"
                  onChange={handleSelectAll}
                  checked={selectedItems.length > 0 && selectedItems.length === tableData.length}
                  style={{ marginLeft: '10px' }}
                />
              </th>
            </tr>
          </thead>
          <tbody>
            {tableData.length > 0 ? (
              tableData.map((row, index) => (
                <tr key={index}>
                  <td style={{ border: '1px solid #ccc', padding: '8px' }}>{row.category}</td>
                  <td style={{ border: '1px solid #ccc', padding: '8px' }}>{row.item}</td>
                  <td style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={selectedItems.includes(row.itemId)}
                      onChange={() => handleCheckboxChange(row.itemId)}
                    />
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={3}
                  style={{ textAlign: 'center', border: '1px solid #ccc', padding: '8px' }}
                >
                  {isUserLoading ? 'Loading...' : 'No Data Found'}
                </td>
              </tr>
            )}
          </tbody>
        </table>

        <div style={{ marginTop: '20px' }}>
          <button
            type='button'
            disabled={loading || selectedItems.length === 0}
            onClick={handleSubmit}
            style={{
              padding: '8px 16px',
              backgroundColor: '#1e88e5',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              opacity: loading || selectedItems.length === 0 ? 0.7 : 1
            }}
          >
            {loading ? 'Processing...' : 'Generate Sankey'}
          </button>
          <button
            type='button'
            onClick={cancel}
            style={{
              marginLeft: '10px',
              padding: '8px 16px',
              backgroundColor: '#e0e0e0',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  )
}

export default WfdItems